/**
 * useListKeyboardNavigation Hook
 *
 * Provides keyboard navigation for list components with arrow keys,
 * Home/End, Enter/Space selection, and Escape to clear focus.
 *
 * @module components/ui/PaginatedList/hooks/useListKeyboardNavigation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { KeyboardEvent, RefObject } from 'react';

interface UseListKeyboardNavigationOptions<T> {
  /** Number of items in the list */
  itemCount: number;
  /** Data items for selection callback */
  data: T[];
  /** Callback when item selected via Enter/Space */
  onItemSelect?: (item: T, index: number) => void;
  /** Enable/disable keyboard navigation */
  enabled?: boolean;
}

interface UseListKeyboardNavigationReturn {
  /** Currently focused item index (-1 if none) */
  focusedIndex: number;
  /** Manually set focused index */
  setFocusedIndex: (index: number) => void;
  /** Keydown handler for list container */
  handleKeyDown: (event: KeyboardEvent) => void;
  /** Ref to attach to list container */
  listRef: RefObject<HTMLDivElement | null>;
}

/**
 * Hook for keyboard navigation in lists
 *
 * @example
 * ```tsx
 * const { focusedIndex, handleKeyDown, listRef } = useListKeyboardNavigation({
 *   itemCount: items.length,
 *   data: items,
 *   onItemSelect: (item) => console.log('Selected:', item),
 * });
 * ```
 */
export function useListKeyboardNavigation<T>({
  itemCount,
  data,
  onItemSelect,
  enabled = true,
}: UseListKeyboardNavigationOptions<T>): UseListKeyboardNavigationReturn {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Store values in refs to avoid callback recreation
  const focusedIndexRef = useRef(focusedIndex);
  focusedIndexRef.current = focusedIndex;

  const dataRef = useRef(data);
  dataRef.current = data;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || itemCount === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => (prev >= itemCount - 1 ? 0 : prev + 1));
          break;

        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => (prev <= 0 ? itemCount - 1 : prev - 1));
          break;

        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;

        case 'End':
          event.preventDefault();
          setFocusedIndex(itemCount - 1);
          break;

        case 'Enter':
        case ' ': {
          event.preventDefault();
          const currentIndex = focusedIndexRef.current;
          if (currentIndex >= 0 && currentIndex < itemCount) {
            const item = dataRef.current[currentIndex];
            if (item !== undefined && onItemSelect) {
              onItemSelect(item, currentIndex);
            }
          }
          break;
        }

        case 'Escape':
          event.preventDefault();
          setFocusedIndex(-1);
          listRef.current?.blur();
          break;
      }
    },
    [enabled, itemCount, onItemSelect]
  );

  // Reset focus when item count changes
  useEffect(() => {
    if (focusedIndex >= itemCount) {
      setFocusedIndex(itemCount > 0 ? itemCount - 1 : -1);
    }
  }, [itemCount, focusedIndex]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.querySelector(
        '[data-focused="true"]'
      ) as HTMLElement | null;
      focusedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex]);

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
    listRef,
  };
}
